import { supabase } from '@/integrations/supabase/client';

export interface AdminThemeSettingsPayload {
  action: 'fetch' | 'save';
  orgId: string;
  themeSettings?: Record<string, unknown>;
}

export interface ThemeSettingsRecord {
  id?: string;
  theme_name?: string | null;
  button_style?: string | null;
  heading_font?: string | null;
  body_font?: string | null;
  layout_style?: string | null;
  primary_color?: string | null;
  button_text_color?: string | null;
}

export interface AdminThemeSettingsResponse {
  themeSettings?: ThemeSettingsRecord | null;
  error?: string;
}

type ThemeSettingsWrite = Partial<{
  organization_id?: string;
  theme_name: string;
  button_style: string;
  heading_font: string;
  body_font: string;
  layout_style: string;
  primary_color: string | null;
  button_text_color: string | null;
}>;

const stringValue = (value: unknown, fallback: string) =>
  typeof value === 'string' && value.length > 0 ? value : fallback;

const nullableStringValue = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : null);

const isMissingColumnError = (error: unknown) =>
  Boolean(error && typeof error === 'object' && 'code' in error && (error as { code?: string }).code === '42703');

export const invokeAdminThemeSettings = async (
  body: AdminThemeSettingsPayload
): Promise<AdminThemeSettingsResponse> => {
  const { data, error } = await supabase.functions.invoke<AdminThemeSettingsResponse>('admin-theme-settings', {
    body,
  });

  if (error) {
    if (error.message?.toLowerCase().includes('failed to send a request')) {
      return invokeThemeSettingsFallback(body);
    }

    throw new Error(error.message || 'Theme settings request failed');
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data ?? {};
};

const invokeThemeSettingsFallback = async (
  body: AdminThemeSettingsPayload
): Promise<AdminThemeSettingsResponse> => {
  if (body.action === 'fetch') {
    let { data, error } = await supabase
      .from('theme_settings')
      .select('*')
      .eq('organization_id', body.orgId)
      .limit(1)
      .maybeSingle();

    if (isMissingColumnError(error)) {
      const fallback = await supabase.from('theme_settings').select('*').limit(1).maybeSingle();
      data = fallback.data;
      error = fallback.error;
    }

    if (error) throw new Error(error.message || 'Failed to fetch theme settings');
    return { themeSettings: data };
  }

  const themeSettings = body.themeSettings ?? {};
  let payload: ThemeSettingsWrite = {
    organization_id: body.orgId,
    theme_name: stringValue(themeSettings.theme_name, 'classic'),
    button_style: stringValue(themeSettings.button_style, 'rounded'),
    heading_font: stringValue(themeSettings.heading_font, 'Palo'),
    body_font: stringValue(themeSettings.body_font, 'DM Sans'),
    layout_style: stringValue(themeSettings.layout_style, 'classic'),
    primary_color: nullableStringValue(themeSettings.primary_color),
    button_text_color: nullableStringValue(themeSettings.button_text_color),
  };

  let { data: existing, error: existingError } = await supabase
    .from('theme_settings')
    .select('id')
    .eq('organization_id', body.orgId)
    .limit(1)
    .maybeSingle();

  if (isMissingColumnError(existingError)) {
    const fallback = await supabase.from('theme_settings').select('id').limit(1).maybeSingle();
    existing = fallback.data;
    existingError = fallback.error;
    const { organization_id, layout_style, button_text_color, ...legacyPayload } = payload;
    payload = legacyPayload;
  }

  if (existingError) throw new Error(existingError.message || 'Failed to load current theme settings');

  const query = existing
    ? supabase.from('theme_settings').update(payload as never).eq('id', existing.id).select('*').single()
    : supabase.from('theme_settings').insert(payload as never).select('*').single();

  const { data: saved, error: saveError } = await query;
  if (saveError) throw new Error(saveError.message || 'Failed to save theme settings');

  return { themeSettings: saved };
};